import API_BASE_URL from '../config';
import React, { useState, useEffect } from "react";
import { toast } from 'react-toastify';

const ManageWebsite = () => {
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // --- CONTACT / GENERAL ---
    const [phoneNumber, setPhoneNumber] = useState("");
    const [email, setEmail] = useState("");
    const [instagramHandle, setInstagramHandle] = useState("");
    const [instagramLink, setInstagramLink] = useState("");
    const [facebookLink, setFacebookLink] = useState("");
    const [youtubeLink, setYoutubeLink] = useState("");
    const [pinterestLink, setPinterestLink] = useState("");

    // --- HERO SECTION ---
    const [heroTitleLine1, setHeroTitleLine1] = useState("");
    const [heroTitleLine2, setHeroTitleLine2] = useState("");
    const [heroSubtext, setHeroSubtext] = useState("");
    const [heroImages, setHeroImages] = useState([]); // [{_id, image, caption}]

    // --- STATS BAR ---
    const [statPiecesCount, setStatPiecesCount] = useState("");
    const [statDeliveryCount, setStatDeliveryCount] = useState("");

    // --- ABOUT SECTION ---
    const [aboutTitleLine1, setAboutTitleLine1] = useState("");
    const [aboutTitleLine2, setAboutTitleLine2] = useState("");
    const [aboutStory, setAboutStory] = useState("");
    const [aboutImage, setAboutImage] = useState("");

    // --- DELIVERY INFO ---
    const [deliveryEstimateText, setDeliveryEstimateText] = useState("");

    // --- TESTIMONIALS ---
    const [testimonials, setTestimonials] = useState([]); // [{_id, name, location, rating, message}]
    const [newTestimonial, setNewTestimonial] = useState({ name: "", location: "", rating: 5, message: "" });

    // --- FOOTER ---
    const [footerDescription, setFooterDescription] = useState("");

    useEffect(() => {
        fetchWebsiteData();
    }, []);

    const fetchWebsiteData = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_BASE_URL}/website-data`);
            const data = await res.json();

            setPhoneNumber(data.phoneNumber || "");
            setEmail(data.email || "");
            setInstagramHandle(data.instagramHandle || "");
            setInstagramLink(data.instagramLink || "");
            setFacebookLink(data.facebookLink || "");
            setYoutubeLink(data.youtubeLink || "");
            setPinterestLink(data.pinterestLink || "");

            setHeroTitleLine1(data.heroTitleLine1 || "Dress Your");
            setHeroTitleLine2(data.heroTitleLine2 || "Dream.");
            setHeroSubtext(data.heroSubtext || "");
            setHeroImages(data.heroImages || []);

            setStatPiecesCount(data.statPiecesCount || "70+");
            setStatDeliveryCount(data.statDeliveryCount || "500+");

            setAboutTitleLine1(data.aboutTitleLine1 || "Born from");
            setAboutTitleLine2(data.aboutTitleLine2 || "Pinterest-worthy visions");
            setAboutStory(data.aboutStory || "");
            setAboutImage(data.aboutImage || "");

            setDeliveryEstimateText(data.deliveryEstimateText || "");

            setTestimonials(data.testimonials || []);

            setFooterDescription(data.footerDescription || "");
        } catch (error) {
            console.error("Error fetching website data:", error);
            toast.error("Failed to load website data");
        } finally {
            setLoading(false);
        }
    };

    // --- IMAGE HELPERS (Base64, same pattern as AddArticle) ---
    const handleHeroImageUpload = (files) => {
        if (!files) return;
        Array.from(files).forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => {
                setHeroImages(prev => [...prev, { image: reader.result, caption: "" }]);
            };
            reader.readAsDataURL(file);
        });
    };

    const removeHeroImage = (index) => {
        setHeroImages(prev => prev.filter((_, i) => i !== index));
    };

    const updateHeroCaption = (index, caption) => {
        setHeroImages(prev => prev.map((h, i) => i === index ? { ...h, caption } : h));
    };

    const handleAboutImageUpload = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => setAboutImage(reader.result);
        reader.readAsDataURL(file);
    };

    // --- TESTIMONIAL HELPERS ---
    const addTestimonial = () => {
        if (!newTestimonial.name.trim() || !newTestimonial.message.trim()) {
            toast.error("Please fill in name and message for the testimonial");
            return;
        }
        setTestimonials(prev => [...prev, { ...newTestimonial }]);
        setNewTestimonial({ name: "", location: "", rating: 5, message: "" });
    };

    const removeTestimonial = (index) => {
        setTestimonials(prev => prev.filter((_, i) => i !== index));
    };

    const updateTestimonialField = (index, field, value) => {
        setTestimonials(prev => prev.map((t, i) => i === index ? { ...t, [field]: value } : t));
    };

    // --- SAVE EVERYTHING ---
    const handleSave = async () => {
        setIsSubmitting(true);
        try {
            const payload = {
                phoneNumber,
                email,
                instagramHandle,
                instagramLink,
                facebookLink,
                youtubeLink,
                pinterestLink,

                heroTitleLine1,
                heroTitleLine2,
                heroSubtext,
                heroImages,

                statPiecesCount,
                statDeliveryCount,

                aboutTitleLine1,
                aboutTitleLine2,
                aboutStory,
                aboutImage,

                deliveryEstimateText,

                testimonials,

                footerDescription,
            };

            const response = await fetch(`${API_BASE_URL}/website-data`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                toast.success("✅ Website data updated successfully");
            } else {
                const result = await response.json();
                toast.error("❌ Error: " + (result.message || "Something went wrong"));
            }
        } catch (error) {
            console.error("Save error:", error);
            toast.error("❌ Server Error: Could not connect to the backend.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="container py-5 text-center">
                <p className="text-muted">Loading website data...</p>
            </div>
        );
    }

    return (
        <div className="container py-5">
            <div className="row justify-content-center">
                <div className="col-lg-10">

                    {/* PAGE HEADER */}
                    <div className="d-flex align-items-center mb-4">
                        <h2 className="fw-bold mb-0">Manage <span className="text-primary">Website</span></h2>
                    </div>

                    <div className="card shadow-sm border-0 p-4">

                        {/* CONTACT INFO */}
                        <h5 className="fw-bold text-secondary mb-3 border-bottom pb-2">📞 Contact & Social Links</h5>
                        <div className="row g-3 mb-4">
                            <div className="col-md-4">
                                <div className="form-floating">
                                    <input className="form-control bg-light border-0" placeholder="Phone" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} />
                                    <label>WhatsApp / Phone Number (e.g. 918968034645)</label>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-floating">
                                    <input className="form-control bg-light border-0" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
                                    <label>Email</label>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-floating">
                                    <input className="form-control bg-light border-0" placeholder="Instagram Handle" value={instagramHandle} onChange={(e) => setInstagramHandle(e.target.value)} />
                                    <label>Instagram Handle (e.g. @sufna)</label>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-floating">
                                    <input className="form-control bg-light border-0" placeholder="Instagram Link" value={instagramLink} onChange={(e) => setInstagramLink(e.target.value)} />
                                    <label>Instagram URL</label>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-floating">
                                    <input className="form-control bg-light border-0" placeholder="Facebook Link" value={facebookLink} onChange={(e) => setFacebookLink(e.target.value)} />
                                    <label>Facebook URL</label>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-floating">
                                    <input className="form-control bg-light border-0" placeholder="YouTube Link" value={youtubeLink} onChange={(e) => setYoutubeLink(e.target.value)} />
                                    <label>YouTube URL</label>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-floating">
                                    <input className="form-control bg-light border-0" placeholder="Pinterest Link" value={pinterestLink} onChange={(e) => setPinterestLink(e.target.value)} />
                                    <label>Pinterest URL</label>
                                </div>
                            </div>
                        </div>

                        {/* HERO SECTION */}
                        <h5 className="fw-bold text-secondary mb-3 border-bottom pb-2">🏠 Homepage Hero</h5>
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input className="form-control bg-light border-0" placeholder="Hero Line 1" value={heroTitleLine1} onChange={(e) => setHeroTitleLine1(e.target.value)} />
                                    <label>Hero Title Line 1 (e.g. "Dress Your")</label>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input className="form-control bg-light border-0" placeholder="Hero Line 2" value={heroTitleLine2} onChange={(e) => setHeroTitleLine2(e.target.value)} />
                                    <label>Hero Title Line 2 / Italic (e.g. "Dream.")</label>
                                </div>
                            </div>
                            <div className="col-12">
                                <div className="form-floating">
                                    <textarea className="form-control bg-light border-0" placeholder="Hero Subtext" style={{ height: '90px' }} value={heroSubtext} onChange={(e) => setHeroSubtext(e.target.value)}></textarea>
                                    <label>Hero Subtext / Intro Paragraph</label>
                                </div>
                            </div>
                        </div>

                        {/* HERO IMAGES */}
                        <div className="mb-4">
                            <label className="fw-bold mb-2 d-block">Hero / Banner Images</label>
                            <div
                                className="border border-2 rounded-3 d-flex flex-column align-items-center justify-content-center p-4 bg-white"
                                style={{ borderStyle: 'dashed', minHeight: '100px', cursor: 'pointer' }}
                                onClick={() => document.getElementById('heroImageInput').click()}
                            >
                                <i className="bi bi-images fs-2"></i>
                                <p className="mb-0">Click to upload hero images</p>
                                <input
                                    type="file"
                                    id="heroImageInput"
                                    className="d-none"
                                    multiple
                                    accept="image/*"
                                    onChange={(e) => handleHeroImageUpload(e.target.files)}
                                />
                            </div>

                            {heroImages.length > 0 && (
                                <div className="d-flex flex-wrap gap-3 mt-3">
                                    {heroImages.map((h, i) => (
                                        <div key={h._id || i} className="position-relative border rounded p-2" style={{ width: '160px' }}>
                                            <img src={h.image} alt="hero" style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '6px' }} />
                                            <input
                                                type="text"
                                                className="form-control form-control-sm bg-light border-0 mt-2"
                                                placeholder="Caption (optional)"
                                                value={h.caption || ""}
                                                onChange={(e) => updateHeroCaption(i, e.target.value)}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeHeroImage(i)}
                                                className="btn btn-danger btn-sm position-absolute top-0 end-0 py-0 px-1"
                                                style={{ fontSize: '10px' }}
                                            >✖</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* STATS BAR */}
                        <h5 className="fw-bold text-secondary mb-3 border-bottom pb-2">📊 Stats Bar</h5>
                        <div className="row g-3 mb-4">
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input className="form-control bg-light border-0" placeholder="Pieces Count" value={statPiecesCount} onChange={(e) => setStatPiecesCount(e.target.value)} />
                                    <label>Curated Pieces (e.g. "70+")</label>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input className="form-control bg-light border-0" placeholder="Delivery Count" value={statDeliveryCount} onChange={(e) => setStatDeliveryCount(e.target.value)} />
                                    <label>Delivery Count (e.g. "500+")</label>
                                </div>
                            </div>
                        </div>

                        {/* ABOUT SECTION */}
                        <h5 className="fw-bold text-secondary mb-3 border-bottom pb-2">📖 About / Story Section</h5>
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input className="form-control bg-light border-0" placeholder="About Line 1" value={aboutTitleLine1} onChange={(e) => setAboutTitleLine1(e.target.value)} />
                                    <label>About Title Line 1 (e.g. "Born from")</label>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-floating">
                                    <input className="form-control bg-light border-0" placeholder="About Line 2" value={aboutTitleLine2} onChange={(e) => setAboutTitleLine2(e.target.value)} />
                                    <label>About Title Line 2 / Italic</label>
                                </div>
                            </div>
                            <div className="col-12">
                                <div className="form-floating">
                                    <textarea className="form-control bg-light border-0" placeholder="Our Story" style={{ height: '140px' }} value={aboutStory} onChange={(e) => setAboutStory(e.target.value)}></textarea>
                                    <label>Our Story (full paragraph)</label>
                                </div>
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="fw-bold mb-2 d-block">About Section Image</label>
                            <div
                                className="border border-2 rounded-3 d-flex flex-column align-items-center justify-content-center p-3 bg-white"
                                style={{ borderStyle: 'dashed', minHeight: '90px', cursor: 'pointer' }}
                                onClick={() => document.getElementById('aboutImageInput').click()}
                            >
                                {aboutImage ? (
                                    <img src={aboutImage} alt="about" style={{ maxHeight: '120px', objectFit: 'cover', borderRadius: '6px' }} />
                                ) : (
                                    <>
                                        <i className="bi bi-image fs-2"></i>
                                        <p className="mb-0">Click to upload about image</p>
                                    </>
                                )}
                                <input
                                    type="file"
                                    id="aboutImageInput"
                                    className="d-none"
                                    accept="image/*"
                                    onChange={(e) => handleAboutImageUpload(e.target.files[0])}
                                />
                            </div>
                        </div>

                        {/* DELIVERY INFO */}
                        <h5 className="fw-bold text-secondary mb-3 border-bottom pb-2">🚚 Delivery Estimate Text</h5>
                        <div className="mb-4">
                            <div className="form-floating">
                                <textarea className="form-control bg-light border-0" placeholder="Delivery Estimate" style={{ height: '100px' }} value={deliveryEstimateText} onChange={(e) => setDeliveryEstimateText(e.target.value)}></textarea>
                                <label>Delivery Estimate Text</label>
                            </div>
                        </div>

                        {/* TESTIMONIALS */}
                        <h5 className="fw-bold text-secondary mb-3 border-bottom pb-2">💬 Testimonials</h5>
                        <div className="mb-3">
                            {testimonials.map((t, i) => (
                                <div key={t._id || i} className="row g-2 mb-2 align-items-center p-2 bg-light rounded">
                                    <div className="col-md-3">
                                        <input type="text" className="form-control form-control-sm bg-white border-0" placeholder="Name" value={t.name} onChange={(e) => updateTestimonialField(i, "name", e.target.value)} />
                                    </div>
                                    <div className="col-md-2">
                                        <input type="text" className="form-control form-control-sm bg-white border-0" placeholder="Location" value={t.location || ""} onChange={(e) => updateTestimonialField(i, "location", e.target.value)} />
                                    </div>
                                    <div className="col-md-1">
                                        <select className="form-select form-select-sm bg-white border-0" value={t.rating} onChange={(e) => updateTestimonialField(i, "rating", Number(e.target.value))}>
                                            {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}★</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-5">
                                        <input type="text" className="form-control form-control-sm bg-white border-0" placeholder="Message" value={t.message} onChange={(e) => updateTestimonialField(i, "message", e.target.value)} />
                                    </div>
                                    <div className="col-md-1 text-end">
                                        <button type="button" className="btn btn-sm text-danger" onClick={() => removeTestimonial(i)}>✖</button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* ADD NEW TESTIMONIAL */}
                        <div className="row g-2 mb-4 p-3 border rounded">
                            <div className="col-md-3">
                                <input type="text" className="form-control form-control-sm bg-light border-0" placeholder="Name" value={newTestimonial.name} onChange={(e) => setNewTestimonial(prev => ({ ...prev, name: e.target.value }))} />
                            </div>
                            <div className="col-md-2">
                                <input type="text" className="form-control form-control-sm bg-light border-0" placeholder="Location" value={newTestimonial.location} onChange={(e) => setNewTestimonial(prev => ({ ...prev, location: e.target.value }))} />
                            </div>
                            <div className="col-md-1">
                                <select className="form-select form-select-sm bg-light border-0" value={newTestimonial.rating} onChange={(e) => setNewTestimonial(prev => ({ ...prev, rating: Number(e.target.value) }))}>
                                    {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}★</option>)}
                                </select>
                            </div>
                            <div className="col-md-4">
                                <input type="text" className="form-control form-control-sm bg-light border-0" placeholder="Message" value={newTestimonial.message} onChange={(e) => setNewTestimonial(prev => ({ ...prev, message: e.target.value }))} />
                            </div>
                            <div className="col-md-2">
                                <button type="button" className="btn btn-sm btn-outline-primary w-100" onClick={addTestimonial}>+ Add Testimonial</button>
                            </div>
                        </div>

                        {/* FOOTER */}
                        <h5 className="fw-bold text-secondary mb-3 border-bottom pb-2">🦶 Footer</h5>
                        <div className="mb-4">
                            <div className="form-floating">
                                <textarea className="form-control bg-light border-0" placeholder="Footer Description" style={{ height: '90px' }} value={footerDescription} onChange={(e) => setFooterDescription(e.target.value)}></textarea>
                                <label>Footer Description</label>
                            </div>
                        </div>

                        {/* SAVE BUTTON */}
                        <div className="d-grid gap-2 mt-2">
                            <button
                                type="button"
                                className="btn btn-primary py-3 fw-bold shadow-sm"
                                onClick={handleSave}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? "Saving..." : "Save Website Data"}
                            </button>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManageWebsite;